import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CryptoService, encryptData, decryptData } from '../../../utils/crypto';

// List of endpoints that don't use encryption (only cloudlab service endpoints)
const NON_ENCRYPTED_ENDPOINTS = [
   // Add any cloudlab endpoints that don't need encryption here
   
];

// Initialize crypto service
let cryptoInitialized = false;

const initializeCrypto = async () => {
   if (!cryptoInitialized && process.env.REACT_APP_CRYPTO_SECRET) {
       try {
           await CryptoService.init(process.env.REACT_APP_CRYPTO_SECRET);
           cryptoInitialized = true;
       } catch (error) {
           console.error('Crypto initialization failed:', error);
       }
   }
};

// Initialize immediately
initializeCrypto();

const USE_ENCRYPTION = process.env.REACT_APP_USE_ENCRYPTION === 'true';

// Helper to determine environment and set baseUrl
const getCloudlabBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:3003/dev';
    }
    return process.env.REACT_APP_CLOUDLAB_SERVICE_BASE_URL;
};

// Base query with JWT handling
const baseQuery = fetchBaseQuery({
    baseUrl: getCloudlabBaseUrl(),
    prepareHeaders: (headers, { getState }) => {
        // Get the JWT from Redux state
        const token = getState().user.jwt;
        
        // If we have a token, add it to the headers
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }
        
        // Add default headers
        headers.set('Content-Type', 'application/json');
        headers.set('Accept', '*/*');
        
        return headers;
    },
});

// Custom base query that handles encryption/decryption
export const cloudlabBaseQuery = async (args, api, extraOptions) => {
    // If the request has a body, encrypt it
    if (args.body) {
        args.body = encryptData(args.body);
    }

    // Get the response
    const response = await baseQuery(args, api, extraOptions);

    // If there's an error, return it
    if (response.error) {
        return response;
    }

    try {
        // If the response has data, decrypt it
        if (response.data) {
            response.data = decryptData(response.data);
        }

        return response;
    } catch (error) {
        return {
            error: {
                status: 'CUSTOM_ERROR',
                data: 'Encryption/Decryption failed',
            },
        };
    }
};

export const cloudlabBaseQueryWithEncryption = async (args, api, extraOptions) => {
    if (!USE_ENCRYPTION) {
        // Just use the base query, no encryption/decryption
        return baseQuery(args, api, extraOptions);
    }

    // Ensure crypto is initialized
    if (!cryptoInitialized) {
        await initializeCrypto();
    }

    // Check if this is a non-encrypted endpoint
    const isNonEncryptedEndpoint = NON_ENCRYPTED_ENDPOINTS.some(endpoint =>
        args.url?.includes(endpoint)
    );

    // Encrypt request body if needed
    if (!isNonEncryptedEndpoint && args.body && cryptoInitialized) {
        try {
            const encrypted = await CryptoService.encrypt(args.body);
            args.body = encrypted;
            args.headers = {
                ...args.headers,
                'Content-Type': 'application/json'
            };
        } catch (error) {
            console.error('Request encryption failed:', error);
        }
    }

    // Make the request
    const result = await cloudlabBaseQuery(args, api, extraOptions);

    // Handle decryption of response
    if (!isNonEncryptedEndpoint && cryptoInitialized && result.data) {
        try {
            let shouldDecrypt = false;
            let payloadToDecrypt = null;

            // Case 1: Response data has encrypted fields directly
            if (result.data?.encryptedData && result.data?.IV && result.data?.authTag) {
                payloadToDecrypt = JSON.stringify({
                    IV: result.data.IV,
                    encryptedData: result.data.encryptedData,
                    authTag: result.data.authTag
                });
                shouldDecrypt = true;
            }
            // Case 2: Response data.data has encrypted fields
            else if (result.data?.data?.encryptedData &&
                     result.data?.data?.IV &&
                     result.data?.data?.authTag) {
                payloadToDecrypt = JSON.stringify({
                    IV: result.data.data.IV,
                    encryptedData: result.data.data.encryptedData,
                    authTag: result.data.data.authTag
                });
                shouldDecrypt = 'nested';
            }
            // Case 3: Response data is a string (encrypted JSON)
            else if (typeof result.data === 'string') {
                payloadToDecrypt = result.data;
                shouldDecrypt = true;
            }

            if (shouldDecrypt && payloadToDecrypt) {
                const decrypted = await CryptoService.decrypt(payloadToDecrypt);
                
                if (shouldDecrypt === 'nested') {
                    result.data = {
                        ...result.data,
                        data: decrypted
                    };
                } else {
                    result.data = decrypted;
                }
            }
        } catch (error) {
            console.error('Response decryption failed:', error);
        }
    }

    return result;
}; 